import { FC, Fragment, ReactNode } from "react";
import { Box, RadioGroup, useMediaQuery, useTheme } from "@material-ui/core";
import { useStyles } from "../../useStyles";
import { ChangeNames, ChangeTypes } from "../../clients/FirebaseClient/types";
import { useFormModel } from "../core/FormModelHelpes";
import { FormControlLabel } from "../core/FormControlLabel";
import { Radio } from "../core/Radio";
import { makeStyles, createStyles, FormControl, Grid } from "@material-ui/core";
import {
    RemovalIcon,
} from "../core/icons/Icons";
import { InfoBox } from "../core/InfoBox";
import { Colors } from "../../styles";

const useFormControlStyles = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            position: "relative",
            width: "100%",
            flexDirection: "column",
            height: "165px",
            borderRadius: "12px",
            padding: "24px 24px 24px 24px",
            boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.1)",
        },
        containerSmall: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            height: "100%",
            minHeight: "96px",
            borderRadius: "12px",
            padding: "24px 24px 24px 24px",
            boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.1)",
        },
        icon: {
            width: "72px",
            height: "72px",
            alignSelf: "center",
            padding: "5px 4px 4px 5px",
            borderRadius: "8px",
            objectFit: "contain",
        },
        iconSmall: {
            width: "56px",
            height: "56px",
            borderRadius: "8px",
            objectFit: "contain",
        },
        formControl: {
            position: "absolute",
            bottom: 20,
            fontFamily: "innogyWEB",
            fontSize: "22px",
            fontWeight: 300,
            lineHeight: 1.27,
            color: "#3c3732",
        },
        formControlSmall: {
            fontFamily: "innogyWEB",
            marginTop: "0px",
            fontSize: "22px",
            fontWeight: 300,
            lineHeight: 1.27,
            color: "#3c3732",
        },
        formControlContainer: {
            width: "100%",
        }
    }),
);

const convertValue = (value: ChangeTypes[ChangeNames.PRODUCT_TYPES]["value"]): string => {
    if (value.newConnection) {
        return "newConnection";
    } else if (value.modification) {
        return "modification";
    } else if (value.removal) {
        return "removal";
    } else if (value.charger) {
        return "charger";
    } else if (value.increase) {
        return "increase";
    } else if (value.adjustableLoad) {
        return "adjustableLoad";
    }
    return "";
};

interface ProductTypesTileProps {
    icon: ReactNode;
    value: keyof ChangeTypes[ChangeNames.PRODUCT_TYPES]["value"];
    label: string;
    error: boolean;
    size?: 'default' | 'double';
}
const ProductTypesTile: FC<ProductTypesTileProps> = (props: ProductTypesTileProps) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    return (
        <Grid item xs={12} lg={props.size === 'double' ? 8 : 4}>
            {
                isSmallScreen ? (
                    <ProductTypesTileSmall {...props} />
                ) : (
                    <ProductTypesTileBig {...props} />
                )
            }
        </Grid>
    )
};
const ProductTypesTileBig: FC<ProductTypesTileProps> = (props: ProductTypesTileProps) => {
    const formControlStyles = useFormControlStyles();
    const { values } = useFormModel<ChangeNames.PRODUCT_TYPES>();
    return (
        <div
            className={formControlStyles.container}
            style={{
                backgroundColor:
                    values.productTypes.value[props.value] === true ? "white" : "#f8f8f8",
                border: "solid 1px #dddddd",
            }}
        >
            <div className={formControlStyles.icon}>{props.icon}</div>
            <FormControlLabel
                value={props.value}
                className={formControlStyles.formControl}
                control={<Radio />}
                label={props.label}
                error={props.error}
            />
        </div>
    );
};
const ProductTypesTileSmall: FC<ProductTypesTileProps> = (props: ProductTypesTileProps) => {
    const formControlStyles = useFormControlStyles();
    const { values } = useFormModel<ChangeNames.PRODUCT_TYPES>();
    return (
        <Grid item>
            <div
                className={formControlStyles.containerSmall}
                style={{
                    backgroundColor:
                        values.productTypes.value[props.value] === true ? "white" : "#f8f8f8",
                    border: "solid 1px #dddddd",
                }}
            >
                <FormControlLabel
                    value={props.value}
                    className={formControlStyles.formControlSmall}
                    control={<Radio />}
                    label={props.label}
                    error={props.error}
                />
                <div className={formControlStyles.iconSmall}>{props.icon}</div>
            </div>
        </Grid>
    );
};

export const ProductTypesForm: FC = () => {
    const { values, setValues, submitChange } = useFormModel<ChangeNames.PRODUCT_TYPES>();
    const classes = useStyles();
    const formClasses = useFormControlStyles();
    const selection = convertValue(values.productTypes.value);

    return (
        <Fragment>
            <InfoBox title="Hinweis" children={
                <>
                    <p>
                        Dieses Portal unterstützt nur noch Demontage-Anträge und zeigt die Übersicht aller Netzanschluss-Anträge, die vor dem 27.11.2024 erstellt wurden.
                    </p>
                    <p>
                        Nutzen Sie unser neues <a style={{color: Colors.mellowYellow}} href="https://meine.syna.de/home?service=netzanschluss" rel="noreferrer" target="_blank">meine Syna Portal</a> für:
                    </p>
                    <ul>
                        <li>Neuen Netzanschluss beantragen</li>
                        <li>Änderung am Netzanschluss beantragen</li>
                        <li>Leistungserhöhung am Netzanschluss beantragen</li>
                        <li>Verbrauchsgeräte anmelden (z.B. Ladeeinrichtung, Wärmepumpe, Gerät zur Raumkühlung, Speicherheizung, Sauna, etc.)</li>
                    </ul>
                </>
            }></InfoBox>
            <FormControl component="fieldset" className={`${formClasses.formControlContainer}`}>
                <h4 className={classes.h4} style={{ marginBottom: 20, marginTop: 0 }}>
                    Art der Maßnahme wählen
                </h4>
                <RadioGroup
                    style={{ marginTop: 12 }}
                    value={selection}
                    onChange={(e, v) => {
                        const newValues = setValues((values) => {
                            values.productTypes.value.newConnection =
                                v.indexOf("newConnection") >= 0;
                            values.productTypes.value.modification = v.indexOf("modification") >= 0;
                            values.productTypes.value.removal = v.indexOf("removal") >= 0;
                            values.productTypes.value.increase = v.indexOf("increase") >= 0;
                            values.productTypes.value.adjustableLoad = v.indexOf("adjustableLoad") >= 0;
                        });
                        submitChange(newValues);
                    }}
                >
                    <Grid container spacing={2}>
                        <ProductTypesTile
                            icon={<RemovalIcon size="size56" />}
                            value="removal"
                            label="Demontage"
                            error={values.productTypes.error !== null}
                        />
                    </Grid>
                </RadioGroup>
            </FormControl>
            <Box height="62px" />
        </Fragment>
    );
};
